// @spec NIS2-STR-025
// Page route for 72-hour incident notification

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  useCreateIncidentNotification,
  useDownloadNotificationPdf,
  useIncidentNotification,
  usePreviewNotification,
  useSubmitNotification,
  type NotificationPreviewRequest,
} from '@/features/nis2/api/incident-notification';
import { IncidentNotificationForm, IncidentNotificationPreview } from '@/features/nis2/components';
import type { IncidentNotificationFormData } from '@/features/nis2/schemas/incident-notification-schema';
import { CirtEmailSubmissionDialog } from '@/features/incidents/components/CirtEmailSubmissionDialog';
import {
  buildCirtAttachmentFilename,
  openCirtMailDraft,
} from '@/features/incidents/utils/cirt-email';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/nis2/incidents/$incidentId/notification')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.nis2IncidentNotification72h'),
  }),
  component: IncidentNotificationPage,
});

type ViewMode = 'form' | 'preview';

function IncidentNotificationPage() {
  const { t } = useTranslation(['nis2', 'common']);
  const { incidentId } = Route.useParams();
  const navigate = useNavigate();
  const { organizations } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);
  const orgId = activeOrg?.id;

  const [viewMode, setViewMode] = React.useState<ViewMode>('form');
  const [previewContent, setPreviewContent] = React.useState<string>('');
  const [previewWordCount, setPreviewWordCount] = React.useState<number>(0);
  const [previewPages, setPreviewPages] = React.useState<number>(1);
  const [showSubmissionDialog, setShowSubmissionDialog] = React.useState(false);

  // Fetch existing notification
  const {
    data: notification,
    isLoading: isLoadingNotification,
    error: notificationError,
  } = useIncidentNotification({
    orgId: orgId ?? '',
    incidentId,
    enabled: Boolean(orgId),
  });

  // Mutations
  const createMutation = useCreateIncidentNotification({
    orgId: orgId ?? '',
    incidentId,
  });

  const previewMutation = usePreviewNotification({
    orgId: orgId ?? '',
    incidentId,
  });

  const submitMutation = useSubmitNotification({
    orgId: orgId ?? '',
    incidentId,
  });

  const downloadMutation = useDownloadNotificationPdf({
    orgId: orgId ?? '',
    incidentId,
  });

  // Calculate deadline (72 hours from incident discovery)
  // For now use a placeholder - in real app this would come from incident data
  const deadline = React.useMemo(() => {
    if (notification?.deadline) {
      return new Date(notification.deadline);
    }
    // Default to 72 hours from now if no notification exists yet
    return new Date(Date.now() + 72 * 60 * 60 * 1000);
  }, [notification?.deadline]);

  // Handle save draft
  const handleSave = async (data: IncidentNotificationFormData) => {
    try {
      await createMutation.mutateAsync({
        authorityName: data.authorityName,
        incidentSummary: data.incidentSummary,
        severityAssessment: data.severityAssessment,
        impactDescription: data.impactDescription,
        affectedServices: data.affectedServices,
        crossBorderImpact: data.crossBorderImpact,
        mitigationMeasures: data.mitigationMeasures,
        nextSteps: data.nextSteps,
        updateFromEarlyWarning: data.updateFromEarlyWarning,
        ...(data.contactEmail && { contactEmail: data.contactEmail }),
        ...(data.affectedUsersCount !== undefined && {
          affectedUsersCount: data.affectedUsersCount,
        }),
        ...(data.affectedCountries?.length && { affectedCountries: data.affectedCountries }),
        ...(data.indicatorsOfCompromise && { indicatorsOfCompromise: data.indicatorsOfCompromise }),
        ...(data.rootCausePreliminary && { rootCausePreliminary: data.rootCausePreliminary }),
      });

      toast.success(t('nis2:toast.draftSaved'), {
        description: t('notification.draftSavedDescription'),
      });
    } catch (error) {
      toast.error(t('nis2:toast.failedToSaveDraft'), {
        description: error instanceof Error ? error.message : t('notification.unexpectedError'),
      });
    }
  };

  // Handle preview
  const handlePreview = async (data: IncidentNotificationFormData) => {
    try {
      const previewRequest: NotificationPreviewRequest = {
        authorityName: data.authorityName,
        incidentSummary: data.incidentSummary,
        severityAssessment: data.severityAssessment,
        impactDescription: data.impactDescription,
        affectedServices: data.affectedServices,
        crossBorderImpact: data.crossBorderImpact,
        mitigationMeasures: data.mitigationMeasures,
        nextSteps: data.nextSteps,
        ...(data.contactEmail && { contactEmail: data.contactEmail }),
        ...(data.affectedUsersCount !== undefined && {
          affectedUsersCount: data.affectedUsersCount,
        }),
        ...(data.affectedCountries?.length && { affectedCountries: data.affectedCountries }),
        ...(data.indicatorsOfCompromise && { indicatorsOfCompromise: data.indicatorsOfCompromise }),
        ...(data.rootCausePreliminary && { rootCausePreliminary: data.rootCausePreliminary }),
      };

      const preview = await previewMutation.mutateAsync(previewRequest);
      setPreviewContent(preview.content);
      setPreviewWordCount(preview.wordCount);
      setPreviewPages(preview.estimatedPages);
      setViewMode('preview');
    } catch (error) {
      toast.error(t('nis2:toast.failedToGeneratePreview'), {
        description: error instanceof Error ? error.message : t('notification.unexpectedError'),
      });
    }
  };

  // Handle submit
  const handleSubmit = async (submissionReference?: string) => {
    const attachmentName = buildCirtAttachmentFilename('72h', incidentId, 'pdf');

    try {
      await downloadMutation.mutateAsync();
      openCirtMailDraft({
        formType: '72h',
        incidentLabel: incidentId,
        attachmentName,
      });
      await submitMutation.mutateAsync({
        content: previewContent || (notification?.content ?? ''),
        ...(submissionReference && { submissionReference }),
        submissionMethod: 'email',
      });

      toast.success(t('nis2:toast.notificationSubmitted'), {
        description: t('notification.markedAsSubmitted'),
      });
      setShowSubmissionDialog(false);
    } catch (error) {
      toast.error(t('nis2:toast.failedToSubmitNotification'), {
        description: error instanceof Error ? error.message : t('notification.unexpectedError'),
      });
    }
  };

  // Handle download PDF
  const handleDownloadPdf = async () => {
    try {
      await downloadMutation.mutateAsync();
      toast.success(t('nis2:toast.pdfDownloaded'), {
        description: t('notification.pdfDownloadedDescription'),
      });
    } catch (error) {
      toast.error(t('nis2:toast.failedToDownloadPDF'), {
        description: error instanceof Error ? error.message : t('notification.unexpectedError'),
      });
    }
  };

  // Handle cancel
  const handleCancel = () => {
    void navigate({ to: '/nis2/dashboard' });
  };

  // Handle edit (switch back to form view)
  const handleEdit = () => {
    setViewMode('form');
  };

  // Loading state
  if (!orgId || isLoadingNotification) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (notificationError && !notificationError.message.includes('404')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('common:routeErrors.error')}</AlertTitle>
            <AlertDescription>{notificationError.message}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Determine if notification is read-only (already submitted)
  const isReadOnly =
    notification?.status === 'submitted' || notification?.status === 'acknowledged';

  // If notification exists and is submitted, show preview mode
  const showExistingPreview = notification && (isReadOnly || viewMode === 'preview');

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl">
        {/* Back button */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => void navigate({ to: '/nis2/dashboard' })}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('notification.backToDashboard')}
          </Button>
        </div>

        {/* Page title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">{t('notification.pageTitle')}</h1>
          <p className="text-muted-foreground mt-2">{t('notification.pageDescription')}</p>
        </div>

        {/* Content */}
        {notification && showExistingPreview ? (
          <IncidentNotificationPreview
            content={previewContent || notification.content}
            deadline={deadline}
            incidentNumber={incidentId}
            authorityName={notification.authorityName}
            wordCount={previewWordCount}
            estimatedPages={previewPages}
            {...(notification.submittedAt && { submittedAt: new Date(notification.submittedAt) })}
            onEdit={handleEdit}
            onSubmit={() => {
              setShowSubmissionDialog(true);
            }}
            onDownloadPdf={() => {
              void handleDownloadPdf();
            }}
            isSubmitting={submitMutation.isPending}
            isDownloading={downloadMutation.isPending}
            isReadOnly={isReadOnly}
          />
        ) : (
          <IncidentNotificationForm
            incidentId={incidentId}
            nis2IncidentId={notification?.nis2IncidentId ?? ''}
            deadline={deadline}
            onSave={handleSave}
            onPreview={(data) => {
              void handlePreview(data);
            }}
            onCancel={handleCancel}
            isSaving={createMutation.isPending || previewMutation.isPending}
            {...(notification?.earlyWarningContent && {
              earlyWarningContent: notification.earlyWarningContent,
            })}
            {...(notification && { existingNotification: notification })}
          />
        )}

        <CirtEmailSubmissionDialog
          open={showSubmissionDialog}
          onOpenChange={setShowSubmissionDialog}
          formType="72h"
          incidentLabel={incidentId}
          attachmentName={buildCirtAttachmentFilename('72h', incidentId, 'pdf')}
          isSubmitting={submitMutation.isPending || downloadMutation.isPending}
          onSubmit={(data) => {
            void handleSubmit(data.submissionReference || undefined);
          }}
        />
      </div>
    </div>
  );
}
